import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { TsBadgeModule, TsButtonModule, TsPageHeaderModule } from 'tsui';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import {
  FinancialStatementEntry,
  ReportPeriod,
  CategoryAccount,
  FinancialStatementTypes,
  IncomeStatementEntry,
} from '../../models';
import { AssetManagementGridService } from '../../services/asset-management-grid.service';
import { GridApi, GridOptions } from 'ag-grid-enterprise';
import { AsyncPipe } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { ReportPeriodSelectorComponent } from '../report-period-selector/report-period-selector.component';

@Component({
  selector: 'financial-statement-grid',
  standalone: true,
  imports: [AgGridAngular, TsPageHeaderModule, TsBadgeModule, TsButtonModule, ReportPeriodSelectorComponent, AsyncPipe],
  templateUrl: './financial-statement-grid.component.html',
  styleUrl: '../../styles/asset-management-shared-styles.scss',
  styles: [
    `
      :host {
        @apply w-full h-full;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FinancialStatementGridComponent implements OnInit {
  @Input({ required: true }) set financialStatement(val: FinancialStatementEntry[]) {
    if (val) {
      this.rowData = AssetManagementGridService.flattenTreeAndAddPath(val, { key: 'display', value: 'group' }).filter(
        // TODO remove this once the API doesn't return `aggregation` type any longer
        (val) => val.display !== 'aggregation',
      );
    }

    AssetManagementGridService.autoSizeColumns(this.gridApi);
  }
  @Input({ required: true }) mode: FinancialStatementTypes;
  @Input({ required: true }) reportPeriod: ReportPeriod;
  @Input() set loading(val: boolean) {
    this._loading.next(val);
  }
  @Input() displayHeader = true;

  @Output() reportPeriodChanged = new EventEmitter<ReportPeriod>();

  _loading = new BehaviorSubject<boolean>(true);
  rowData: FinancialStatementEntry[] = [];
  private gridApi!: GridApi<any>;
  readonly assetManagementGridService = inject(AssetManagementGridService);
  readonly getDataPath = AssetManagementGridService.getDataPath;
  readonly gridOptions: GridOptions = AssetManagementGridService.getTreeDataGridCommonOptions();
  readonly autoGroupColumnDef: ColDef = AssetManagementGridService.getAutoGroupColDef('Account', 'name');
  readonly balanceSheetColDefs: ColDef[] = [
    {
      valueGetter: ({ data }) => {
        return AssetManagementGridService.getNumericValue(data?.periods?.[0]?.forward_balance);
      },
      headerName: 'Beginning Balance',
      aggFunc: 'sum',
      valueFormatter: (params) =>
        this.assetManagementGridService.formatCurrencyToAccounting(params?.data?.periods?.[0]?.forward_balance, true),
    },
    {
      valueGetter: ({ data }) => {
        return AssetManagementGridService.getNumericValue(data?.periods?.[0]?.current_balance);
      },
      headerName: 'Current Balance',
      aggFunc: 'sum',
      valueFormatter: (params) =>
        this.assetManagementGridService.formatCurrencyToAccounting(params?.data?.periods?.[0]?.current_balance, true),
    },
    {
      valueGetter: ({ data }) => {
        return AssetManagementGridService.getNumericValue(data?.periods?.[0]?.variation);
      },
      headerName: 'Net Change',
      aggFunc: 'sum',
      valueFormatter: (params) =>
        this.assetManagementGridService.formatCurrencyToAccounting(params?.data?.periods?.[0]?.variation, true),
    },
  ].map((colDef) => ({ ...colDef, ...AssetManagementGridService.getTreeDataGridCommonColDef() }));
  readonly incomeStatementColDefs: ColDef[] = [
    {
      headerName: 'Period to Date',
      aggFunc: 'sum',
      valueGetter: ({ data }) => {
        return AssetManagementGridService.getNumericValue(data?.periods?.[0]?.period_to_date);
      },
      valueFormatter: (params) =>
        this.assetManagementGridService.formatCurrencyToAccounting(params?.data?.periods?.[0]?.period_to_date, true),
    },
    {
      headerName: 'Period Budget',
      aggFunc: 'sum',
      valueGetter: ({ data }: { data: IncomeStatementEntry }) => {
        return AssetManagementGridService.getNumericValue(data?.periods?.[0]?.period_to_date_budget);
      },
      valueFormatter: (params) =>
        this.assetManagementGridService.formatCurrencyToAccounting(
          params?.data?.periods?.[0]?.period_to_date_budget,
          true,
        ),
    },
    {
      headerName: '%',
      valueGetter: ({ data }) => data?.periods?.[0]?.period_to_date_variance,
      valueFormatter: (params) =>
        this.assetManagementGridService.formatVariance(params?.data?.periods?.[0]?.period_to_date_variance),
    },
    {
      headerName: 'Year to Date',
      aggFunc: 'sum',
      valueGetter: ({ data }) => {
        return AssetManagementGridService.getNumericValue(data?.periods?.[0]?.year_to_date);
      },
      valueFormatter: (params) =>
        this.assetManagementGridService.formatCurrencyToAccounting(params?.data?.periods?.[0]?.year_to_date, true),
    },
    {
      headerName: 'Year Budget',
      aggFunc: 'sum',
      valueGetter: ({ data }: { data: IncomeStatementEntry }) => {
        return AssetManagementGridService.getNumericValue(data?.periods?.[0]?.year_to_date_budget);
      },
      valueFormatter: (params) =>
        this.assetManagementGridService.formatCurrencyToAccounting(
          params?.data?.periods?.[0]?.year_to_date_budget,
          true,
        ),
    },
    {
      valueGetter: ({ data }) => data?.periods?.[0]?.year_to_date_variance,
      headerName: '%',
      valueFormatter: (params) =>
        this.assetManagementGridService.formatVariance(params?.data?.periods?.[0]?.year_to_date_variance),
    },
  ].map((colDef) => ({ ...colDef, ...AssetManagementGridService.getTreeDataGridCommonColDef() }));

  ngOnInit() {
    this._loading.subscribe((loading) => {
      if (this.gridApi) {
        this.gridApi.setGridOption('loading', loading);
      }
    });
  }

  onGridReady(params: GridReadyEvent<CategoryAccount>) {
    this.gridApi = params.api;

    this._loading.next(this._loading.value);
    AssetManagementGridService.autoSizeColumns(this.gridApi);
  }
}
